import {
    ScoopDialog,
    ScoopText,
    ScoopSearchBar,
    ScoopColumn,
    ScoopDivider,
    useToast,
} from '../../../../ui'
import { useCallback, useMemo, useState } from 'react'
import { SOURCES_OPTIONS } from '../../SourcesOptions'
import { EVENTS, trackEvent } from '../../../../../events'
import { EnvelopeIcon, DatabaseIcon, UploadIcon } from '@phosphor-icons/react'
import { debounce } from 'lodash'
import { useModal, MODALS } from '../../../../modals'
import { useUserInfo } from '../../../../../api/hooks/user/useUserInfo'
import sharedStyles from '../NewDatasetModal.module.scss'
import styles from './CreateDatasetModal.module.scss'

export const CreateDatasetModal = ({
    open,
    close,
    handleSelectSource,
    isOnboarding,
    workspaceMetadata,
}) => {
    const { user } = useUserInfo()
    const isScoopAdmin = user?.accountID === 'A1'

    const SOURCES = useMemo(
        () =>
            SOURCES_OPTIONS.filter(
                (source) => source.name !== 'Database' && source.name !== 'Other'
            )
                .map((source) =>
                    source.adminOnly && !isScoopAdmin ? { ...source, disabled: true } : source
                )
                .sort((a, b) =>
                    !a.disabled === !b.disabled ? a.name.localeCompare(b.name) : a.disabled ? 1 : -1
                ),
        [isScoopAdmin]
    )

    const [search, setSearch] = useState('')
    const [sources, setSources] = useState(SOURCES)
    const toast = useToast()
    const integrationRequestModal = useModal(MODALS.COMMON.REQUEST_INTEGRATION)

    const googleSheetsSource = SOURCES_OPTIONS.filter((s) => s.name === 'Google Sheets')[0]

    const OTHER_SOURCES = [
        {
            name: 'Database',
            icon: DatabaseIcon,
            onClick: () => {
                trackEvent(EVENTS.DATASET.CONNECT_INTEGRATION_ATTEMPT, {
                    api: {
                        connector: 'Database',
                        'action ': 'started new connector flow',
                    },
                })
                const databaseSource = SOURCES_OPTIONS.find((source) => source.name === 'Database')
                handleSelectSource('Application', databaseSource)
            },
        },
        {
            name: 'Email',
            icon: EnvelopeIcon,
            onClick: () => handleSelectSource('Data Email'),
        },
    ]

    const debouncedLogEvent = useCallback(
        debounce((value) => {
            trackEvent(EVENTS.DATASET.CONNECT_INTEGRATION_UNAVAILABLE, {
                api: {
                    connector: `${value}`,
                    action: 'started new connector flow for unavailable connector',
                },
            })
        }, 2000),
        []
    )

    const handleSearch = (e) => {
        const value = e.target.value
        setSearch(value)

        const filtered = SOURCES.filter((source) =>
            source.name.toLowerCase().includes(value.toLowerCase())
        )
        setSources(filtered)

        if (filtered.length === 0) {
            debouncedLogEvent(value)
        } else {
            debouncedLogEvent.cancel()
        }
    }

    const handleSourceSelection = (source) => {
        if (!source.isInstalled || (source.isInstalled && !source.allowSingleInstall)) {
            if (source.emailRedirect) {
                toast.info(`Connect ${source.name} by forwarding your report emails to Scoop`)
                handleSelectSource('Data Email')
                return
            } else if (source.disabled) {
                toast.info(
                    'This connector is not available in this product release and is coming soon!'
                )
                trackEvent(EVENTS.DATASET.CONNECT_INTEGRATION_UNAVAILABLE, {
                    api: {
                        connector: `${source.name}`,
                        action: 'started new connector flow for unavailable connector',
                    },
                })
                return
            } else {
                trackEvent(EVENTS.DATASET.CONNECT_INTEGRATION_ATTEMPT, {
                    api: {
                        connector: source.name,
                        'action ': 'started new connector flow',
                    },
                })
            }
            handleSelectSource('Application', source)
        }
    }

    const renderTab0 = () => {
        return (
            <ScoopColumn
                className={isOnboarding ? styles.tab0ContainerWithHeight : styles.tab0Container}
            >
                <ScoopText className={styles.descriptionText}>
                    {isOnboarding
                        ? 'Or select from one of our various integrations below'
                        : 'Integrate your business systems for continuous, automated data sync with our growing list of integrations'}
                </ScoopText>
                <div className={styles.searchBarContainer}>
                    <ScoopSearchBar
                        value={search}
                        onChange={handleSearch}
                        placeholder={'Search integrations (e.g., Salesforce, Google Sheets)'}
                    />
                </div>
                <div
                    className={`${sharedStyles.connectionsContainer} ${
                        isOnboarding
                            ? styles.connectionsContainerTab0Onboarding
                            : styles.connectionsContainerTab0
                    }`}
                >
                    {sources.length > 0 ? (
                        sources.map((source, index) =>
                            isOnboarding && source.name === 'Google Sheets' ? null : (
                                <div
                                    key={index}
                                    className={`${sharedStyles.connectionsBox} ${styles.sourceBox} ${
                                        source.disabled
                                            ? styles.sourceBoxComingSoon
                                            : source.isInstalled && source.allowSingleInstall
                                              ? styles.sourceBoxDisabled
                                              : styles.sourceBoxEnabled
                                    }`}
                                    onClick={
                                        source.disabled
                                            ? undefined
                                            : () => handleSourceSelection(source)
                                    }
                                >
                                    <div
                                        className={styles.sourceIconBackground}
                                        style={{ '--source-bg-color': source.backgroundColor }}
                                    >
                                        <img
                                            src={source.icon}
                                            alt={source.name}
                                            className={sharedStyles.sourceIcon}
                                        />
                                    </div>
                                    <ScoopText className={styles.sourceNameText}>
                                        {source.preferredSpelling || source.name}
                                    </ScoopText>
                                    {source.disabled && (
                                        <ScoopText className={styles.comingSoonBadge}>
                                            Coming soon
                                        </ScoopText>
                                    )}
                                    {source.isInstalled && source.allowSingleInstall && (
                                        <ScoopText className={styles.alreadyInstalledBadge}>
                                            Already installed
                                        </ScoopText>
                                    )}
                                </div>
                            )
                        )
                    ) : (
                        <ScoopColumn className={styles.noResultsContainer}>
                            <ScoopText className={styles.noResultsTitle}>
                                No results found for "{search}"
                            </ScoopText>
                            <ScoopText>
                                Check out 'Other ways to connect' to explore additional ways to
                                connect your data. If you need any additional assistance, please
                                contact us at{' '}
                                <a
                                    href="mailto:support@scoopanalytics.com?subject=Connection Request&body=Hi, I'd like to request a connection for..."
                                    className={styles.supportLink}
                                    onClick={() =>
                                        trackEvent(EVENTS.ACCOUNT.CONTACT_SUPPORT, {
                                            source: 'dataset_no_results',
                                        })
                                    }
                                >
                                    support@scoopanalytics.com
                                </a>
                                .
                            </ScoopText>
                        </ScoopColumn>
                    )}
                </div>
                <ScoopText className={styles.requestIntegrationText}>
                    Looking for something else?{' '}
                    <span
                        onClick={async () => {
                            const result = await integrationRequestModal.show({
                                title: 'Request Integration',
                            })
                            if (result?.success) {
                                toast.success('Your request has been submitted successfully!')
                            }
                        }}
                        className={styles.requestIntegrationLink}
                    >
                        Request an integration
                    </span>
                </ScoopText>
            </ScoopColumn>
        )
    }

    const renderTab1 = () => {
        return (
            <ScoopColumn className={styles.tab1Container}>
                <ScoopText className={styles.descriptionText}>
                    {isOnboarding
                        ? 'Get started by uploading a structured dataset via File Upload or Google Sheets'
                        : ''}
                </ScoopText>
                {!isOnboarding && (
                    <ScoopText className={styles.additionalWaysTitle}>
                        Additional ways to connect
                    </ScoopText>
                )}
                <div
                    className={`${sharedStyles.connectionsContainer} ${
                        isOnboarding
                            ? styles.connectionsContainerTab1Onboarding
                            : styles.connectionsContainerTab1
                    }`}
                >
                    {isOnboarding && (
                        <div
                            className={`${sharedStyles.connectionsBox} ${styles.sourceBox} ${styles.sourceBoxEnabled}`}
                            onClick={() => handleSelectSource('File Upload')}
                        >
                            <UploadIcon size={40} className={styles.phosphorIcon} />
                            <ScoopText className={styles.sourceNameText}>File Upload</ScoopText>
                        </div>
                    )}
                    {OTHER_SOURCES.map((source, index) => {
                        const IconComponent = source.icon
                        return isOnboarding ? null : (
                            <div
                                key={index}
                                className={`${sharedStyles.connectionsBox} ${styles.sourceBox} ${styles.sourceBoxEnabled}`}
                                onClick={source.onClick}
                            >
                                <IconComponent size={40} className={styles.phosphorIcon} />
                                <ScoopText className={styles.sourceNameText}>
                                    {source.name}
                                </ScoopText>
                            </div>
                        )
                    })}
                    {isOnboarding && (
                        <div
                            className={`${sharedStyles.connectionsBox} ${styles.sourceBox} ${
                                googleSheetsSource.isInstalled &&
                                googleSheetsSource.allowSingleInstall
                                    ? styles.sourceBoxDisabled
                                    : styles.sourceBoxEnabled
                            }`}
                            onClick={() => handleSourceSelection(googleSheetsSource)}
                        >
                            <div
                                className={styles.sourceIconBackground}
                                style={{ '--source-bg-color': googleSheetsSource.backgroundColor }}
                            >
                                <img
                                    src={googleSheetsSource.icon}
                                    alt={googleSheetsSource.name}
                                    className={sharedStyles.sourceIcon}
                                />
                            </div>
                            <ScoopText className={styles.sourceNameText}>
                                {googleSheetsSource.preferredSpelling || googleSheetsSource.name}
                            </ScoopText>
                            {googleSheetsSource.isInstalled &&
                                googleSheetsSource.allowSingleInstall && (
                                    <ScoopText className={styles.alreadyInstalledBadge}>
                                        Already installed
                                    </ScoopText>
                                )}
                        </div>
                    )}
                </div>
            </ScoopColumn>
        )
    }

    return (
        <>
            {isOnboarding ? (
                <>
                    {renderTab1()}
                    {renderTab0()}
                </>
            ) : (
                <>
                    <ScoopDialog
                        style={{ minWidth: 980, height: '90vh' }}
                        containerStyle={{ height: '90vh' }}
                        contentStyle={{ maxHeight: 'none' }}
                        open={open}
                        onClose={close}
                        title={'Connect Data Source'}
                        className={styles.dialogContainer}
                    >
                        <ScoopColumn className={styles.dialogContentContainer}>
                            <ScoopText className={styles.fileUploadDescription}>
                                Upload a file directly — no setup needed
                            </ScoopText>
                            <div
                                className={`${sharedStyles.connectionsBox} ${styles.sourceBox} ${styles.sourceBoxEnabled}`}
                                onClick={() => handleSelectSource('File Upload')}
                            >
                                <UploadIcon size={40} className={styles.phosphorIcon} />
                                <ScoopText className={styles.sourceNameText}>File Upload</ScoopText>
                            </div>
                            <ScoopDivider />
                            <ScoopColumn className={styles.dataIntegrationsSection}>
                                <ScoopText className={styles.dataIntegrationsTitle}>
                                    Data Integrations
                                </ScoopText>
                                <div className={styles.dataIntegrationsContent}>{renderTab0()}</div>

                                {renderTab1()}
                            </ScoopColumn>
                        </ScoopColumn>
                    </ScoopDialog>
                </>
            )}
        </>
    )
}
